import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest, HTTPResponse } from '@data/controller/NetworkController';
import { ChurchService, ChurchServiceAttributesBody, ChurchServicePage } from '@interfaces/service.interface';

export default class ServicesController {
  private static baseURL: string = process.env.REACT_APP_SANCTUARY_API_URL || "";

  // A page of a campus's services in one category, newest first. No
  // `categoryID` means the campus's uncategorized services, not every service
  // at the campus. Pass a page's `nextLastServiceID` as `lastServiceID` for the
  // page after it.
  //
  // Only past services are public; `includeUpcoming` adds scheduled ones and
  // needs the Service Manager role.
  static async fetchServices(
    churchID: string,
    campusID: string,
    categoryID: string | null,
    includeUpcoming: boolean,
    lastServiceID: string | null,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchServicePage>> {
    const query = new URLSearchParams();

    if (categoryID) {
      query.set('categoryID', categoryID);
    }

    if (includeUpcoming) {
      query.set('includeUpcoming', 'true');
    }

    if (lastServiceID) {
      query.set('lastServiceID', lastServiceID);
    }

    const queryString = query.toString();

    return await NetworkController.performRequestAt<ChurchServicePage>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.GET,
        `/v1/church/${churchID}/campus/${campusID}/library/service${queryString ? `?${queryString}` : ''}`
      ),
      auth.consumeRefreshToken
    );
  }

  // One service by ID, wherever it's filed. An upcoming service is only
  // returned to staff who manage services; to anyone else, like a missing one,
  // it's a 404.
  static async fetchService(
    churchID: string,
    serviceID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchService>> {
    return await NetworkController.performRequestAt<ChurchService>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/library/service/${serviceID}`),
      auth.consumeRefreshToken
    );
  }

  // Creates a service at `attributes.campusID`. Needs the Service Manager
  // role, and the same uploaded thumbnail as an update.
  static async createService(
    churchID: string,
    attributes: ChurchServiceAttributesBody,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchService>> {
    return await NetworkController.performRequestAt<ChurchService>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST,
        `/v1/church/${churchID}/campus/${attributes.campusID}/library/service`,
        { attributes }
      ),
      auth.consumeRefreshToken
    );
  }

  // Replaces the service's attributes with `attributes`, which must be
  // complete. Needs the Service Manager role. The thumbnail has to be one
  // uploaded through uploadLibraryThumbnail.
  //
  // The path names the campus the service ends up at, which Sanctuary requires
  // to match `attributes.campusID`; a different one from its current campus
  // moves it.
  static async updateService(
    churchID: string,
    service: ChurchService,
    attributes: ChurchServiceAttributesBody,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchService>> {
    return await NetworkController.performRequestAt<ChurchService>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PATCH,
        `/v1/church/${churchID}/campus/${attributes.campusID}/library/service/${service.id}`,
        { attributes }
      ),
      auth.consumeRefreshToken
    );
  }

  // Permanently removes a service. Needs the Service Manager role; a service
  // that's already gone is a 404.
  static async deleteService(
    churchID: string,
    serviceID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<void>> {
    return await NetworkController.performRequestAt<void>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.DELETE, `/v1/church/${churchID}/library/service/${serviceID}`),
      auth.consumeRefreshToken
    );
  }
}
